// client/src/pages/schedule/ScheduleRead.js
import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import FileAttachment from '../../components/FileAttachment/FileAttachment';
import { eventTypeMap, attendanceMap } from '../../utils/commonUtils';
import { getScheduleExtras, deleteSchedule, updateAttendance, approveSchedule, rejectSchedule, recallSchedule } from '../../api/scheduleApi';
import TodoRegisterLayer from '../../components/todo/TodoRegisterLayer';
import { TODO_REF_TYPE, TODO_REF_TABLE } from '../../api/todoApi';
import './ScheduleRead.css';

const ScheduleRead = ({ data, onClose, onEdit, onRefresh }) => {
  const { token, user } = useContext(AuthContext);
  const [extras, setExtras] = useState({ files: [], targets: [] });
  const [showTodoLayer, setShowTodoLayer] = useState(false);
  const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false); // 펼침 상태 관리
  const todoRegBtnRef = useRef(null);

  //console.log("user.userid==>", user.userid)
  useEffect(() => {
    if (data?.id && token) {
      getScheduleExtras(data.id, token)
        .then(res => {
          //console.log("▶ 참석자 데이터:", res.targets);
          setExtras(res);
        })
        .catch(err => console.error("▶ 첨부/참석자 조회 오류:", err));
    }
  }, [data, token]);

  if (!data) return null;

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours < 12 ? '오전' : '오후';
    const hour12 = hours % 12 || 12;
    return `${year}-${month}-${day} ${ampm} ${hour12}:${minutes}:${seconds}`;
  };
  /*
  // 예약일시 포맷 변환
  const formatReservation = (schedule) => {
    const startDate = schedule.start_date;
    const endDate = schedule.end_date;
    const startTime = schedule.start_time ? schedule.start_time.substring(0, 5) : '';
    const endTime = schedule.end_time ? schedule.end_time.substring(0, 5) : '';

    // 날짜가 같으면 한 번만 표시
    if (startDate === endDate) {
      return `${startDate} ${startTime} ~ ${endTime}`;
    } else {
      return `${startDate} ${startTime} ~ ${endDate} ${endTime}`;
    }
  };  
  */
  const handleDelete = async () => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    try {
      await deleteSchedule(data.id, token);
      alert("삭제되었습니다.");
      onClose(true); // ✅ 삭제 성공 시 true 전달
    } catch (err) {
      console.error("삭제 오류:", err);
      alert("삭제 처리 중 오류가 발생했습니다.");
    }
  };

  const handleApprove = async () => {
    try {
      await approveSchedule(data.id, token, 'approve');
      alert("승인되었습니다.");
      if (onClose) onClose(true); // ✅ 갱신 플래그 전달
    } catch (err) {
      console.error("승인 실패:", err);
      alert("승인 중 오류가 발생했습니다.");
    }
  };

  const handleReject = async () => {
    try {
      await rejectSchedule(data.id, token, 'reject');
      alert("반려되었습니다.");
      if (onClose) onClose(true); // ✅ 갱신 플래그 전달
    } catch (err) {
      console.error("반려 실패:", err);
      alert("반려 중 오류가 발생했습니다.");
    }
  };

  const handleDraft = async () => {
    try {
      await recallSchedule(data.id, token, 'recall');
      alert("회수되었습니다.");
      if (onClose) onClose(true); // ✅ 갱신 플래그 전달
    } catch (err) {
      console.error("회수 실패:", err);
      alert("회수 중 오류가 발생했습니다.");
    }
  };

  const handleAttendance = async (isAttend) => {
    try {
      await updateAttendance(data.id, isAttend ? 'Y' : 'N', token, user.userid);
      // ✅ 현재 로그인한 사용자 ID를 target_id로 전달
      alert(isAttend ? "참석 처리되었습니다." : "불참 처리되었습니다.");
      onClose(true);
    } catch (err) {
      console.error(err);
      alert("참석 상태 변경 중 오류가 발생했습니다.");
    }
  };

  // 버튼 렌더링
  const renderButtonsByStatusAndRole = () => {
    let buttons = [];

    if (data.event_type === 'W' && data.status === 'request') {
      if (user.role === 'admin') {
        buttons.push(
          <button key="approve" onClick={handleApprove}>승인</button>,
          <button key="reject" onClick={handleReject}>반려</button>
        );
      }
      if (user.userid === data.writer_id) {
        buttons.push(<button key="draft" onClick={handleDraft}>회수</button>);
      }
    }
    if (
      data.event_type === 'M' &&
      data.status === 'register' &&
      extras.targets.some(t => t.target_id === user.userid)
    ) {
      buttons.push(
        <button key="attend" onClick={() => handleAttendance(true)}>참석</button>,
        <button key="absent" onClick={() => handleAttendance(false)}>불참</button>
      );
    }

    // ✅ 수정 버튼: status가 draft가 아닐 때만 표시
    if ((user.role === 'admin' || user.userid === data.writer_id) && data.status !== 'draft') {
      //if ((user.role === 'admin' || user.userid === data.writer_id)) {
      buttons.push(<button key="edit" onClick={onEdit}>수정</button>);
    }

    // ✅ 삭제 버튼: 관리자 또는 작성자만 표시
    if (user.role === 'admin' || user.userid === data.writer_id) {
      buttons.push(<button key="delete" onClick={handleDelete}>삭제</button>);
    }

    // 닫기 버튼은 항상 표시
    buttons.push(<button key="list" onClick={onClose}>닫기</button>);

    return buttons;
  };

  return (
    <div className="schedule_modal_read">
      {/* 헤더 */}
      <div className="schedule_modal_read_header">
        <h5>일정 조회</h5>
        <button className="close_btn" onClick={onClose}>×</button>
      </div>

      {/* 본문 */}
      <div className="schedule_modal_read_body">
        <section className="schedule_section">
          <h6>기본사항</h6>
          <p><strong>제목 : </strong> {data.title}</p>
          <p><strong>작성자 : </strong> {data.writer_name} / {data.writer_post}({data.dept_name})</p>
          <strong>작성일시 : </strong> {formatDateTime(data.created_at)}
        </section>

        <section className="schedule_section">
          <h6>상세내용</h6>
          <p><strong>일정구분 : </strong> {eventTypeMap[data.event_type]}</p>
          <p><strong>일자 : </strong> {data.start_date} ~ {data.end_date}</p>
          <p><strong>시간 : </strong> {data.start_time} ~ {data.end_time}</p>

          {(data.event_type === 'M' || data.event_type === 'S') && (
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>일정공유 (참석자) : </strong>
                <div className="height-controls">
                  {extras.targets?.length > 4 && (
                    <button
                      type="button"
                      className="btn-height"
                      onClick={() => setIsParticipantsExpanded(!isParticipantsExpanded)}
                      title={isParticipantsExpanded ? "축소" : "펼치기"}
                    >
                      <i className={`fas ${isParticipantsExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                    </button>
                  )}
                </div>
              </div>
              <div
                className={`read_participants_list ${isParticipantsExpanded ? 'expanded' : ''}`}
                style={{
                  maxHeight: isParticipantsExpanded ? 'none' : '125px',
                  overflowY: isParticipantsExpanded ? 'visible' : 'auto'
                }}
              >
                {extras.targets?.length > 0 ? (
                  extras.targets.map((t, idx) => (
                    <div key={idx} className="read_participant_item">
                      <span>
                        {data.event_type === 'M' && (
                          <span className={`badge me-2 ${t.target_attendance === 'Y' ? 'bg-success' : t.target_attendance === 'N' ? 'bg-danger' : 'bg-secondary'}`}>
                            {attendanceMap[t.target_attendance] || '미정'}
                          </span>
                        )}
                        {t.disp_target_name}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted small m-0">참석자가 없습니다.</p>
                )}
              </div>
            </div>
          )}

          {data.event_type === 'W' && (
            <p>
              <strong>일정공유 : </strong>{" "}
              {extras.targets?.map(t => t.disp_target_name).join(', ')}
            </p>
          )}
        </section>

        {data.content && (
          <section className="schedule_section">
            <h6>본문</h6>
            <div
              className="post-content"
              dangerouslySetInnerHTML={{ __html: data.content }}
            />
          </section>
        )}

        {extras.files?.length > 0 && (
          <section className="schedule_section">
            <h6>첨부</h6>
            <FileAttachment files={extras.files} mode="view" />
          </section>
        )}
      </div>

      {/* 푸터 */}
      <div className="schedule_modal_read_footer">
        {/* 왼쪽: To-Do 등록 */}
        <div className="schedule_footer_left" style={{ position: 'relative' }}>
          <button
            ref={todoRegBtnRef}
            className="todo-reg-btn"
            onClick={() => setShowTodoLayer(prev => !prev)}
          >
            To-Do 등록
          </button>
          {showTodoLayer && (
            <TodoRegisterLayer
              direction="up"
              source={{
                ref_type: TODO_REF_TYPE.SCHEDULE,
                ref_table: TODO_REF_TABLE.SCHEDULE,
                ref_id: data.id,
                title: data.title,
              }}
              anchorRef={todoRegBtnRef}
              onClose={() => setShowTodoLayer(false)}

            />
          )}
        </div>
        {/* 오른쪽: 기존 버튼들 */}
        <div className="schedule_footer_right">
          {renderButtonsByStatusAndRole()}
        </div>
      </div>
    </div>
  );
};

export default ScheduleRead;